import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { Dispatch } from 'redux';
import { FormikErrors } from 'formik';
import { HandledError } from '@synergy/api';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../../../../../api';
import { AppState } from '../../../../../../store/models/app-state';
import { ContactPermissions } from '../models/contact-permissions';
import { ContactPermissionsFormValues } from '../models/contact-permissions-form-values';
import { EmailErrorCode } from '../../../../models/email-error-code';
import { SaveResult } from '../../../../../../common/models/save-result';
import { contactPermissionsUpdateRequestSelector } from './contact-detail-permissions-selectors';
import { handleLockContactSuccess } from '../../store/contact-detail-global-actions';

export const CONTACT_DETAIL_PERMISSIONS_GET = 'CONTACT_DETAIL_PERMISSIONS_GET';
export const CONTACT_DETAIL_PERMISSIONS_RECEIVED = 'CONTACT_DETAIL_PERMISSIONS_RECEIVED';
export const CONTACT_DETAIL_PERMISSIONS_UPDATE = 'CONTACT_DETAIL_PERMISSIONS_UPDATE';
export const CONTACT_DETAIL_PERMISSIONS_SAVE = 'CONTACT_DETAIL_PERMISSIONS_SAVE';
export const CONTACT_DETAIL_PERMISSIONS_DISABLE_LOGIN = 'CONTACT_DETAIL_PERMISSIONS_DISABLE_LOGIN';
export const CONTACT_DETAIL_PERMISSIONS_ERROR_DATA = 'CONTACT_DETAIL_PERMISSIONS_ERROR_DATA';
export const CONTACT_DETAIL_PERMISSIONS_INVITE_USER = 'CONTACT_DETAIL_PERMISSIONS_INVITE_USER';
export const CONTACT_DETAIL_PERMISSIONS_RE_INVITE_USER = 'CONTACT_DETAIL_PERMISSIONS_RE_INVITE_USER';
export const CONTACT_DETAIL_PERMISSIONS_INVITE_USER_SUCCESS = 'CONTACT_DETAIL_PERMISSIONS_INVITE_USER_SUCCESS';

export const updateContactPermissions = (
    formData: ContactPermissionsFormValues,
    isDirty: boolean,
    isValid: boolean,
    errors: FormikErrors<ContactPermissionsFormValues>
) => {
    return {
        type: CONTACT_DETAIL_PERMISSIONS_UPDATE,
        formData,
        isDirty,
        isValid,
        errors,
    };
};

const handleErrorData = (errorData?: HandledError<EmailErrorCode>[]) => ({
    type: CONTACT_DETAIL_PERMISSIONS_ERROR_DATA,
    errorData,
});

const permissionsReceived = (permissions: ContactPermissions) => {
    return {
        type: CONTACT_DETAIL_PERMISSIONS_RECEIVED,
        permissions,
    };
};

export const fetchContactPermissions = (contactId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_PERMISSIONS_GET });

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Permissions.get(contactId);

        if (response.success) {
            dispatch(permissionsReceived(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }

        return response;
    };
};

export const savePermissions = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_PERMISSIONS_SAVE });

        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!contactId) {
            return SaveResult.Error;
        }

        const request = contactPermissionsUpdateRequestSelector(getState());
        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Permissions.updatePermissions(
            contactId,
            request
        );

        if (response.success) {
            dispatch(permissionsReceived(response.result));
            return SaveResult.Success;
        }

        if (response.errorHandled) {
            dispatch(handleErrorData(response.handledErrors));
            return SaveResult.FormErrors;
        }
        return SaveResult.Error;
    };
};

export const disableLogin = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_PERMISSIONS_DISABLE_LOGIN });

        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!contactId) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return;
        }

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Permissions.deleteUser(contactId);

        if (!response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return;
        }

        dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.loginDeleted')));
    };
};

export const inviteContact = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({
            type: CONTACT_DETAIL_PERMISSIONS_INVITE_USER,
        });

        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!contactId) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return;
        }

        const request = contactPermissionsUpdateRequestSelector(getState());
        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Permissions.inviteContact(
            contactId,
            request
        );

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.invitationSent')));
            dispatch(permissionsReceived(response.result));
            dispatch(handleLockContactSuccess());

            return SaveResult.Success;
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return SaveResult.Error;
        }
    };
};

export const reInviteContact = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({
            type: CONTACT_DETAIL_PERMISSIONS_RE_INVITE_USER,
        });

        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!contactId) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return;
        }

        const request = contactPermissionsUpdateRequestSelector(getState());
        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Permissions.inviteContact(
            contactId,
            request
        );

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.reInvitationSent')));
            dispatch(permissionsReceived(response.result));

            return SaveResult.Success;
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return SaveResult.Error;
        }
    };
};
